/**
 * Atomic Client Destruction — Phase 1 Blast Radius planner (read-only).
 *
 * Server authority: cp_get_client_blast_radius
 * Never mutates. Does not execute teardown, reverse, void, or GC.
 */

import { can, CAPABILITY, assertCan } from "./permissions";

/** Classification enum used by the planner (and eventual UI). */
export const BLAST_RADIUS_CLASS = Object.freeze({
  DELETE: "DELETE",
  REVERSE: "REVERSE",
  VOID: "VOID",
  DETACH: "DETACH",
  PRESERVE: "PRESERVE",
  EXTERNAL_CLEANUP: "EXTERNAL_CLEANUP",
  BLOCK: "BLOCK",
  UNRESOLVED: "UNRESOLVED",
});

/** Overall readiness flags (may appear together). */
export const BLAST_RADIUS_STATUS = Object.freeze({
  READY: "READY",
  REVERSALS_REQUIRED: "REVERSALS_REQUIRED",
  BLOCKED: "BLOCKED",
  EXTERNAL_CLEANUP_REQUIRED: "EXTERNAL_CLEANUP_REQUIRED",
});

export const BLAST_RADIUS_SCHEMA_VERSION = 1;

export function canInspectClientBlastRadius(actor) {
  return can(actor, CAPABILITY.CLIENT_BLAST_RADIUS);
}

/**
 * Load the authoritative read-only blast-radius plan for a client.
 */
export async function getClientBlastRadius(supabaseClient, clientId, actor) {
  try {
    assertCan(actor, CAPABILITY.CLIENT_BLAST_RADIUS);
  } catch (error) {
    return { data: null, error };
  }

  if (!supabaseClient || !clientId) {
    return {
      data: null,
      error: new Error("getClientBlastRadius: clientId is required"),
    };
  }

  const { data, error } = await supabaseClient.rpc("cp_get_client_blast_radius", {
    p_client_id: clientId,
  });

  return { data, error };
}

/**
 * Summarize planner payload for UI / tests without re-querying.
 */
export function summarizeBlastRadius(payload) {
  if (!payload || typeof payload !== "object") {
    return {
      schemaVersion: null,
      readOnly: true,
      totalRecords: 0,
      overallStatus: BLAST_RADIUS_STATUS.BLOCKED,
      statusFlags: [],
      canExecute: false,
      byClass: {},
      missingCapabilities: [],
      permanentDeleteEligible: false,
      branchIds: [],
    };
  }

  const summary = payload.summary || {};
  const related = payload.related || {};

  return {
    schemaVersion: payload.schema_version ?? null,
    readOnly: payload.read_only !== false,
    totalRecords: Number(summary.total_records_affected || 0),
    overallStatus: summary.overall_status || BLAST_RADIUS_STATUS.BLOCKED,
    statusFlags: Array.isArray(summary.status_flags) ? summary.status_flags : [],
    canExecute: Boolean(summary.can_execute_atomic_destruction),
    byClass: summary.by_class && typeof summary.by_class === "object" ? summary.by_class : {},
    missingCapabilities: Array.isArray(payload.missing_capabilities)
      ? payload.missing_capabilities
      : [],
    permanentDeleteEligible: Boolean(related.permanent_delete_eligible),
    branchIds: Array.isArray(payload.branches)
      ? payload.branches.map((b) => b?.id).filter(Boolean)
      : [],
  };
}

export function findBlastRadiusBranch(payload, branchId) {
  if (!payload || !Array.isArray(payload.branches)) return null;
  return payload.branches.find((b) => b?.id === branchId) || null;
}

export function blastRadiusHasCapabilityGap(payload, code) {
  const gaps = payload?.missing_capabilities;
  if (!Array.isArray(gaps)) return false;
  return gaps.some((g) => g?.code === code);
}

/** Fail closed when planner schema is missing or unsupported. */
export function isSupportedBlastRadiusSchema(payload) {
  return Number(payload?.schema_version) === BLAST_RADIUS_SCHEMA_VERSION;
}

export const BLAST_RADIUS_CLASS_META = Object.freeze({
  [BLAST_RADIUS_CLASS.DELETE]: {
    label: "Will Delete",
    tone: "danger",
    short: "DELETE",
  },
  [BLAST_RADIUS_CLASS.REVERSE]: {
    label: "Will Reverse",
    tone: "warning",
    short: "REVERSE",
  },
  [BLAST_RADIUS_CLASS.VOID]: {
    label: "Will Void",
    tone: "warning",
    short: "VOID",
  },
  [BLAST_RADIUS_CLASS.DETACH]: {
    label: "Will Detach",
    tone: "info",
    short: "DETACH",
  },
  [BLAST_RADIUS_CLASS.PRESERVE]: {
    label: "Will Be Preserved",
    tone: "preserve",
    short: "PRESERVE",
  },
  [BLAST_RADIUS_CLASS.EXTERNAL_CLEANUP]: {
    label: "External Cleanup",
    tone: "external",
    short: "EXTERNAL",
  },
  [BLAST_RADIUS_CLASS.BLOCK]: {
    label: "Cannot Safely Remove",
    tone: "danger",
    short: "BLOCK",
  },
  [BLAST_RADIUS_CLASS.UNRESOLVED]: {
    label: "Unresolved",
    tone: "unresolved",
    short: "UNRESOLVED",
  },
});

export function getBlastRadiusClassMeta(classification) {
  return (
    BLAST_RADIUS_CLASS_META[classification] || {
      label: classification || "Unknown",
      tone: "neutral",
      short: classification || "UNKNOWN",
    }
  );
}

/** Classes with count > 0 for Impact Summary chips. */
export function getMeaningfulClassCounts(byClass = {}) {
  return Object.entries(byClass || {})
    .map(([key, value]) => ({
      classification: key,
      count: Number(value) || 0,
      ...getBlastRadiusClassMeta(key),
    }))
    .filter((row) => row.count > 0);
}

/** Top-level branches with attached records (count > 0), plus empty optional skip. */
export function getVisibleBlastRadiusBranches(payload, { includeEmpty = false } = {}) {
  const branches = Array.isArray(payload?.branches) ? payload.branches : [];
  if (includeEmpty) return branches;
  return branches.filter((b) => Number(b?.count) > 0);
}

export function formatBlastRadiusStateEntries(states = {}) {
  if (!states || typeof states !== "object") return [];
  return Object.entries(states)
    .map(([key, value]) => ({
      key,
      label: String(key).replace(/_/g, " "),
      count: Number(value) || 0,
    }))
    .filter((row) => row.count > 0);
}

export function formatCapabilityCode(code) {
  return String(code || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
